import { useMemo, useState } from 'react';

import { Button, Checkbox, FormControlLabel, FormGroup, Typography } from '@mui/material';
import Step from '@mui/material/Step';
import StepContent from '@mui/material/StepContent';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import { FileUploader } from './FileUploader';
import Footer from './Footer';
import { StyledFormTitle, StyledFormTitleContainer } from '../../../../styles/input-row';
import AdminUsersContainer from '../../AdminUsersContainer';
import { type User, UserRole } from '../../constants/user-roles';

export const FormContainer = styled.div`
    max-width: 750px;
`;

const StyledStepper = styled(Stepper)`
    padding-top: 1rem;
`;

const StyledStepContent = styled(StepContent)`
    padding-left: 1rem;
    padding-right: 1rem;
`;

const StyledButton = styled(Button)`
    margin: 1rem 0;
`;

export const FileUploadForm: React.FC = () => {
    const { t } = useTranslation('portal');
    const [activeStep, setActiveStep] = useState(0);

    const {
        usersForBulkUpload,
        selectedRoles,
        setSelectedRoles,
        bulkUpsertUsers,
        bulkUpsertUsersStatus,
        resetBulkUploadState,
        errorIndexes,
        duplicateIndexes,
        errorsAcknowledged,
        maxUsersLimitExceeded
    } = useContainer(AdminUsersContainer);

    const isChecked = (role: UserRole): boolean => !!selectedRoles.find((selectedRole) => selectedRole === role);

    const handleToggle = (role: UserRole) => {
        setSelectedRoles((previousRoles) => {
            const found = previousRoles.find((prevRole) => prevRole === role);
            if (found) {
                return previousRoles.filter((prevRole) => prevRole !== role);
            }

            return [...previousRoles, role];
        });
    };

    const onSubmit = () => {
        const adminsToSubmit: User[] = usersForBulkUpload.map((admin) => ({ ...admin, roles: selectedRoles }));
        const adminsWithoutErrors: User[] = [];
        adminsToSubmit.forEach((admin, i) => {
            if (errorIndexes.includes(i)) return;
            if (duplicateIndexes.includes(i)) return;
            adminsWithoutErrors.push(admin);
        });

        bulkUpsertUsers(adminsWithoutErrors);
    };

    const onCancel = () => {
        resetBulkUploadState();
    };

    const disableSave = useMemo(() => {
        const hasErrors =
            (duplicateIndexes.length || errorIndexes.length || maxUsersLimitExceeded) && !errorsAcknowledged;
        const disable = usersForBulkUpload.length === 0 || hasErrors;

        return disable;
    }, [
        usersForBulkUpload.length,
        duplicateIndexes.length,
        errorIndexes.length,
        errorsAcknowledged,
        maxUsersLimitExceeded
    ]);

    return (
        <FormContainer>
            <StyledFormTitleContainer>
                <StyledFormTitle>{t('users.fileUpload')}</StyledFormTitle>
            </StyledFormTitleContainer>
            <StyledStepper activeStep={activeStep} orientation="vertical">
                <Step expanded={true} key={0}>
                    <StepLabel>
                        <Typography variant="body1">{t('users.selectUserRoles')}</Typography>
                    </StepLabel>
                    <StyledStepContent>
                        <Typography variant="body2">{t('users.appliedToAll')}</Typography>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        onChange={() => handleToggle(UserRole.Admin)}
                                        checked={isChecked(UserRole.Admin)}
                                        name={UserRole.Admin}
                                    />
                                }
                                label={t('users.userRoles.admin')}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        onChange={() => handleToggle(UserRole.Teacher)}
                                        checked={isChecked(UserRole.Teacher)}
                                        name={UserRole.Teacher}
                                    />
                                }
                                label={t('users.userRoles.teacher')}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        onChange={() => handleToggle(UserRole.InstructionalDesigner)}
                                        checked={isChecked(UserRole.InstructionalDesigner)}
                                        name={UserRole.InstructionalDesigner}
                                    />
                                }
                                label={t('users.userRoles.instructional-designer')}
                            />
                        </FormGroup>
                        {!activeStep && (
                            <StyledButton variant="outlined" onClick={() => setActiveStep(1)}>
                                {t('next')}
                            </StyledButton>
                        )}
                    </StyledStepContent>
                </Step>
                <Step key={1}>
                    <StepLabel>
                        <Typography variant="body1">{t('users.uploadFile')}</Typography>
                    </StepLabel>
                    <StyledStepContent>
                        <FileUploader />
                    </StyledStepContent>
                </Step>
            </StyledStepper>
            <Footer
                onCancel={onCancel}
                onSubmit={onSubmit}
                isLoading={bulkUpsertUsersStatus === 'LOADING'}
                canSubmit={!disableSave}
            />
        </FormContainer>
    );
};
